import { Injectable, inject } from '@angular/core';
import { NewOrderService } from './new-order.service';

/**
 * Service that determines if a user can access a specific checkout step
 * based on required data in sessionStorage.
 *
 * Provides intelligent fallback logic when data is missing:
 * - If plan data exists but zipcode missing, can access payment
 * - If only zipcode exists, can only access plan
 * - Otherwise, fallback to zipcode (step 1)
 */
@Injectable({ providedIn: 'root' })
export class StepPreconditionService {
  private readonly newOrderService = inject(NewOrderService);

  /**
   * Checks if user can access Plan step (step 2).
   * Requires: zipcode must exist in session storage.
   *
   * @returns true if zipcode exists, false otherwise
   */
  canAccessPlan(): boolean {
    const orderData = this.newOrderService.getOrderStorageData();
    return !!orderData?.zipcode;
  }

  /**
   * Checks if user can access Payment step (step 3).
   * Requires: no_of_persons, and no_of_recipes must exist in session storage.
   *
   * @returns true if all required fields exist, false otherwise
   */
  canAccessPayment(): boolean {
    const orderData = this.newOrderService.getOrderStorageData();
    return !!orderData?.no_of_persons && !!orderData?.no_of_recipes;
  }

  /**
   * Determines the fallback route when a guard denies access.
   * Uses intelligent cascade based on available data:
   *
   * - If plan data exists (no_of_persons & no_of_recipes): redirect to /payment
   * - Else if zipcode exists: redirect to /plan
   * - Else: redirect to /purchase (step 1)
   *
   * @returns The route path to redirect to
   */
  getFallbackRoute(): string {
    const orderData = this.newOrderService.getOrderStorageData();

    // If plan data exists, user completed plan step, go to payment
    if (orderData?.no_of_persons && orderData?.no_of_recipes) {
      return '/payment';
    }

    // If zipcode exists, user completed zipcode step, go to plan
    if (orderData?.zipcode) {
      return '/plan';
    }

    // No data, start from step 1
    return '/purchase';
  }
}
